<template>
  <v-card class="w-full" flat :color="$vuetify.theme.currentTheme.navigationColor">
    <div class="form-header">
      <!-- Show form only when checkbox is checked -->
      <ActitiveTabcomponent :tabs="mainTabs" :defaultTab="'create'" @tab-changed="setActiveType">
        <template #tab-buttons>
          <div class="">
            <v-btn small plain @click="cancel">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </template>
        <template #create>
          <div class="form-content" v-if="activeType === 'create'">
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-stepper v-model="step" vertical flat color="navigationColor">
                <v-stepper-items>
                  <!-- Step 1: Basic Info -->
                  <v-stepper-step :complete="step > 1" step="1" editable>
                    <div class="flex items-center justify-between w-full">
                      <span>Basic Info <small>(Mandatory)</small></span>
                      <v-btn-toggle
                        v-model="customerType"
                        mandatory
                        dense
                        color="primary"
                        rounded
                        class="elevation-0 border ml-4"
                        @click.native.stop
                      >
                        <v-btn value="individual" small class="px-3 font-semibold normal-case" height="28">
                          <v-icon small left>mdi-account</v-icon> Individual
                        </v-btn>
                        <v-btn value="company" small class="px-3 font-semibold normal-case" height="28">
                          <v-icon small left>mdi-domain</v-icon> Company
                        </v-btn>
                      </v-btn-toggle>
                    </div>
                  </v-stepper-step>
                  <v-stepper-content step="1">

                    <div class="grid grid-cols-2 gap-2">
                      <v-text-field
                        v-model="crn"
                        label="CRN *"
                        :rules="rules.crn"
                        required
                        hide-details="auto"
                        class="pt-2"
                        outlined
                        dense
                        placeholder="ex : CRN123456"
                      >
                        <template v-slot:append>
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn
                                icon
                                x-small
                                v-bind="attrs"
                                v-on="on"
                                @click="crn = generateCRN()"
                                class="-mr-1"
                              >
                                <v-icon small color="primary">mdi-refresh</v-icon>
                              </v-btn>
                            </template>
                            <span>Regenerate CRN</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                      <v-autocomplete
                        v-model="selectedEntity"
                        :label="`Search Entity Name`"
                        :items="entities"
                        item-text="entityName"
                        :search-input.sync="searchEntity"
                        item-value="_id"
                        @update:search-input="debounceEntity"
                        hide-no-data
                        hide-details="auto"
                        class="pt-2"
                        outlined
                        placeholder="ex : ABC Corporation"
                        dense
                        clearable
                      ></v-autocomplete>

                      <!-- Company-specific fields -->
                      <template v-if="customerType === 'company'">
                        <v-text-field
                          v-model="companyName"
                          label="Company Name *"
                          :rules="[(v) => !v && customerType === 'company' ? 'Company name is required' : true]"
                          required
                          hide-details="auto"
                          outlined
                          dense
                          placeholder="ex : Finfi Technologies Pvt Ltd"
                          class="col-span-2"
                        ></v-text-field>
                      </template>

                      <!-- Individual-specific fields -->
                      <template v-else>
                        <div class="flex justify-between">
                          <v-text-field
                            v-model="firstName"
                            label="First Name"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : John"
                          ></v-text-field>
                          <div class="p-1" />
                          <v-text-field
                            v-model="middleName"
                            label="Middle Name"
                            hide-details
                            outlined
                            placeholder="ex : Doe"
                            dense
                          ></v-text-field>
                          <div class="p-1" />
                          <v-text-field
                            v-model="lastName"
                            label="Last Name"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : Smith"
                          ></v-text-field>
                        </div>
                        <div class="flex justify-between">
                          <v-text-field
                            v-model="fatherName"
                            label="Father's Name"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : James Smith"
                          ></v-text-field>
                          <div class="p-1" />
                          <v-text-field
                            v-model="motherName"
                            label="Mother's Name"
                            hide-details
                            outlined
                            placeholder="ex : Mary Smith"
                            dense
                          ></v-text-field>
                        </div>
                        <v-menu
                          v-model="menu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          class="checkBorder_a"
                        >
                          <template v-slot:activator="{ on }">
                            <v-text-field
                              label="Date of birth"
                              placeholder="ex : 02-01-2023"
                              v-model="dateOfBirthFormat"
                              prepend-inner-icon="mdi-calendar"
                              readonly
                              clearable
                              v-on="on"
                              hide-details
                              outlined
                              dense
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            v-model="dateOfBirth"
                            :max="$dayjs().format('YYYY-MM-DD')"
                            no-title
                            scrollable
                          />
                        </v-menu>
                        <v-select
                          v-model="gender"
                          :items="genderTypes"
                          item-text="text"
                          label="Select Gender"
                          item-value="value"
                          hide-details
                          outlined
                          placeholder="ex : Male"
                          dense
                        ></v-select>
                        <v-select
                          v-model="maritalStatus"
                          :items="['Single', 'Married', 'Divorced', 'Widowed']"
                          label="Marital Status"
                          hide-details
                          outlined
                          placeholder="ex : Single"
                          dense
                        ></v-select>
                        <v-text-field
                          v-model="nationality"
                          label="Nationality/Citizenship"
                          hide-details
                          outlined
                          dense
                          placeholder="ex : Indian"
                        ></v-text-field>
                      </template>

                      <v-select
                        v-model="relatedCategory"
                        :items="[
                          { text: 'Collection Management', value: 'collection' },
                          { text: 'Loan Management', value: 'loan_management' }
                        ]"
                        label="Related Category"
                        hide-details
                        outlined
                        dense
                      ></v-select>
                      <v-select
                        v-model="subCase"
                        :items="subCasesList"
                        item-text="name"
                        item-value="_id"
                        label="Sub Case"
                        hide-details
                        outlined
                        dense
                        clearable
                      ></v-select>
                    </div>
                    <div class="flex items-center justify-end pt-3">
                      <v-btn outlined @click="cancel" style="text-transform: none">Cancel</v-btn>
                      <v-btn
                        @click="step++"
                        class="white--text ml-3"
                        :color="$vuetify.theme.currentTheme.primary"
                        style="text-transform: none"
                        >Next</v-btn
                      >
                    </div>
                  </v-stepper-content>

                  <!-- Step 2: Address Details -->
                  <v-stepper-step :complete="step > 2" step="2" editable>
                    Address Details
                    <small>(Optional)</small>
                  </v-stepper-step>
                  <v-stepper-content step="2">
                    <div
                      v-for="(details, index) in customerDetails"
                      :key="index"
                      class="mb-5 border rounded-lg pa-4"
                    >
                      <div class="flex justify-between items-center mb-3">
                        <p class="font-semibold">{{ `Address ${index + 1}` }}</p>
                        <v-icon
                          size="20"
                          color="red"
                          @click="removeDetails(index)"
                          v-show="customerDetails.length > 1"
                          >mdi-delete</v-icon
                        >
                      </div>
                      <div class="grid grid-cols-2 gap-2">
                        <v-select
                          v-model="details.typeOfAddress"
                          :items="filterAddressTypes(index)"
                          item-text="text"
                          label="Address Type"
                          item-value="value"
                          hide-details
                          outlined
                          dense
                          class="mb-2"
                        />
                        <v-text-field
                          v-model="details.pinCode"
                          label="Pin Code"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: 560001"
                          class="mb-2"
                        />
                        <v-text-field
                          v-model="details.addressLine1"
                          label="Address Line 1"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: 123 Main Street"
                          class="mb-2 col-span-2"
                        />
                        <v-text-field
                          v-model="details.addressLine2"
                          label="Address Line 2"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: Apt 4B, Near Station"
                          class="mb-2 col-span-2"
                        />
                        <v-text-field
                          v-model="details.city"
                          label="City"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: Bangalore"
                          class="mb-2"
                        />
                        <v-text-field
                          v-model="details.state"
                          label="State"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: Karnataka"
                          class="mb-2"
                        />
                        <v-text-field
                          v-model="details.country"
                          label="Country"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: India"
                          class="mb-2"
                        />
                      </div>
                    </div>
                    <div class="flex justify-end mb-3">
                      <v-btn
                        text
                        small
                        color="primary"
                        @click="addCustomerDetails"
                        v-if="showAddressTypeButton"
                        style="text-transform: none"
                      >
                        <v-icon left small>mdi-plus</v-icon> Add Address
                      </v-btn>
                    </div>
                    <div class="flex items-center pt-2 justify-end">
                      <v-btn @click="step--" outlined style="text-transform: none">Back</v-btn>
                      <v-btn
                        @click="step++"
                        class="white--text ml-3"
                        :color="$vuetify.theme.currentTheme.primary"
                        style="text-transform: none"
                        >Next</v-btn
                      >
                    </div>
                  </v-stepper-content>

                  <!-- Step 3: Identity Details -->
                  <v-stepper-step :complete="step > 3" step="3" editable>
                    Identity Details <small>(Recommended)</small>
                  </v-stepper-step>
                  <v-stepper-content step="3">
                    <v-checkbox
                      v-model="fillIdentityDetails"
                      label="Do you want to add identity documents"
                      hide-details
                      class="mt-3 mb-3"
                    ></v-checkbox>

                    <div v-if="fillIdentityDetails">
                      <div
                        v-for="(id, index) in identityDetails"
                        :key="index"
                        class="border rounded-lg p-4 mb-4 bg-gray-50 position-relative"
                      >
                        <div class="flex justify-between items-center mb-4">
                          <h4 class="font-bold text-primary">Document #{{ index + 1 }}</h4>
                          <v-btn
                            icon
                            small
                            color="red"
                            @click="removeIdentityDetail(index)"
                            v-if="identityDetails.length > 1"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>

                        <v-row dense>
                          <v-col cols="12" md="6">
                            <v-select
                              v-model="id.idType"
                              :items="idTypeOptions"
                              label="ID Type"
                              outlined
                              dense
                              hide-details
                              class="mb-3"
                              :disabled="id.isVerfied"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              :value="id.isVerfied ? formatMaskedId(id.idNumber) : id.idNumber"
                              @input="val => { id.idNumber = val ? val.toUpperCase() : ''; id.rawIdNumber = id.idNumber }"
                              :label="id.idType === 'GSTIN' ? 'GSTIN (15 Digits)' : id.idType === 'PAN' ? 'PAN (10 Digits)' : ['Aadhar', 'Aadhaar'].includes(id.idType) ? 'Aadhaar (12 Digits)' : 'ID Number'"
                              :placeholder="id.idType === 'GSTIN' ? 'ex : 27AAAPL1234C1ZV' : id.idType === 'PAN' ? 'ex : AAPCM7699P' : ['Aadhar', 'Aadhaar'].includes(id.idType) ? 'ex : 123456789012' : 'ex : 1234567890'"
                              outlined
                              dense
                              hide-details="auto"
                              class="mb-3"
                              :disabled="id.isVerfied"
                            >
                              <template v-slot:append v-if="!id.isVerfied && (id.idType === 'GSTIN' || id.idType === 'PAN' || ['Aadhar', 'Aadhaar'].includes(id.idType))">
                                <v-btn
                                  x-small
                                  color="primary"
                                  class="normal-case font-semibold px-2"
                                  :loading="id.verifying"
                                  :disabled="!id.idNumber || (id.idType === 'GSTIN' ? id.idNumber.length < 15 : id.idType === 'PAN' ? id.idNumber.length < 10 : id.idNumber.replace(/\D/g, '').length < 12)"
                                  @click="verifyIdentityDoc(id)"
                                >
                                  Verify
                                </v-btn>
                              </template>
                            </v-text-field>
                            <div
                              v-if="id.isVerfied"
                              class="text-[11px] text-green-600 font-semibold flex items-center gap-1 -mt-2 mb-2"
                            >
                              <v-icon x-small color="green">mdi-check-circle</v-icon>
                              Verified: {{ id.verifiedLegalName || id.nameAsperId }}
                            </div>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="id.nameAsperId"
                              label="Name as per ID"
                              outlined
                              dense
                              hide-details
                              class="mb-3"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-text-field
                              v-model="id.issuedBy"
                              label="Issued By"
                              outlined
                              dense
                              hide-details
                              class="mb-3"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-menu
                              v-model="id.issueDateMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-model="id.issueDate"
                                  label="Issue Date"
                                  prepend-inner-icon="mdi-calendar"
                                  readonly
                                  v-on="on"
                                  outlined
                                  dense
                                  hide-details
                                  class="mb-3"
                                  clearable
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="id.issueDate"
                                @input="id.issueDateMenu = false"
                                no-title
                              />
                            </v-menu>
                          </v-col>
                          <v-col cols="12" md="6">
                            <v-menu
                              v-model="id.expiryDateMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                            >
                              <template v-slot:activator="{ on }">
                                <v-text-field
                                  v-model="id.expiryDate"
                                  label="Expiry Date"
                                  prepend-inner-icon="mdi-calendar"
                                  readonly
                                  v-on="on"
                                  outlined
                                  dense
                                  hide-details
                                  class="mb-3"
                                  clearable
                                ></v-text-field>
                              </template>
                              <v-date-picker
                                v-model="id.expiryDate"
                                @input="id.expiryDateMenu = false"
                                no-title
                              />
                            </v-menu>
                          </v-col>
                        </v-row>
                      </div>

                      <v-btn
                        text
                        color="primary"
                        class="mb-4 normal-case"
                        @click="addIdentityDetail"
                        v-if="identityDetails.length < 5"
                      >
                        <v-icon left>mdi-plus</v-icon> Add Another Document
                      </v-btn>
                    </div>

                    <div class="flex items-center pt-2 justify-end">
                      <v-btn @click="step--" style="text-transform: none" outlined>Back</v-btn>
                      <v-btn
                        @click="step++"
                        class="white--text ml-3"
                        :color="$vuetify.theme.currentTheme.primary"
                        style="text-transform: none"
                        >Next</v-btn
                      >
                    </div>
                  </v-stepper-content>

                  <!-- Step 4 for Individual: Employment Details -->
                  <template v-if="customerType === 'individual'">
                    <v-stepper-step :complete="step > 4" step="4" editable>
                      Employment & Income Details <small>(Optional)</small>
                    </v-stepper-step>
                    <v-stepper-content step="4">
                      <v-checkbox
                        v-model="fillEmploymentDetails"
                        label="Do you want to add employment & income details"
                        hide-details
                        class="mt-3 mb-3"
                      ></v-checkbox>

                      <div v-if="fillEmploymentDetails">
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <v-select
                            v-model="employmentStatus"
                            :items="employmentStatusOptions"
                            label="Employment Status"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="Select Employment Status"
                          ></v-select>

                          <v-select
                            v-model="jobType"
                            :items="jobTypes"
                            item-text="text"
                            item-value="value"
                            label="Job / Employment Type"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="Select Job Type"
                          ></v-select>

                          <v-text-field
                            v-model="company"
                            label="Employer / Company Name"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : Infosys / TCS / Self-Employed"
                          ></v-text-field>

                          <v-text-field
                            v-model="designation"
                            label="Designation / Role"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : Senior Software Engineer"
                          ></v-text-field>

                          <v-text-field
                            v-model="industrySector"
                            label="Industry / Sector"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : Information Technology / Banking"
                          ></v-text-field>

                          <v-text-field
                            v-model="employmentDuration"
                            label="Total Work Experience / Duration"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : 3 Years 6 Months"
                          ></v-text-field>

                          <v-text-field
                            v-model="previousEmployment"
                            label="Previous Employer / Experience"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs col-span-1 sm:col-span-2"
                            placeholder="ex : Software Engineer at ABC Tech (2 years)"
                          ></v-text-field>

                          <v-text-field
                            v-model.number="monthlyIncome"
                            type="number"
                            label="Monthly Income (₹)"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : 75000"
                            prepend-inner-icon="mdi-currency-inr"
                            @input="calculateYearlyIncome"
                          ></v-text-field>

                          <v-text-field
                            v-model.number="yearlyIncome"
                            type="number"
                            label="Annual Income (₹)"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : 900000"
                            prepend-inner-icon="mdi-currency-inr"
                          ></v-text-field>

                          <v-text-field
                            v-model.number="taxPerYear"
                            type="number"
                            label="Tax Paid Per Year (₹)"
                            outlined
                            dense
                            hide-details="auto"
                            class="text-xs"
                            placeholder="ex : 50000"
                            prepend-inner-icon="mdi-currency-inr"
                          ></v-text-field>
                        </div>
                      </div>

                      <div class="flex items-center pt-3 justify-end">
                        <v-btn @click="step--" outlined style="text-transform: none">Back</v-btn>
                        <v-btn
                          @click="step++"
                          class="white--text ml-3"
                          :color="$vuetify.theme.currentTheme.primary"
                          style="text-transform: none"
                        >
                          Next
                        </v-btn>
                      </div>
                    </v-stepper-content>
                  </template>

                  <!-- Step 5 (Individual) / Step 4 (Company): Contact Details -->
                  <v-stepper-step :complete="step > contactStepNum" :step="contactStepNum" editable>
                    Contact Details <small>(Optional)</small>
                  </v-stepper-step>
                  <v-stepper-content :step="contactStepNum">
                    <v-checkbox
                      v-model="fillContactInfo"
                      :label="customerType === 'company' ? 'Do you want to add company contact details' : 'Do you want to add contact details'"
                      hide-details
                      class="mt-3 mb-3"
                    ></v-checkbox>

                    <div v-if="fillContactInfo">
                      <!-- Company Contacts Section -->
                      <template v-if="customerType === 'company'">
                        <div class="grid grid-cols-2 gap-2 mb-4">
                          <v-text-field
                            v-model="customerEmail"
                            label="Company Official Email"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : contact@company.com"
                          ></v-text-field>
                          <v-text-field
                            v-model="customerMobile[0]"
                            label="Company Phone Number"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : 9876543210"
                          ></v-text-field>
                        </div>

                        <div class="mb-3">
                          <div class="flex items-center justify-between mb-2">
                            <span class="text-sm font-semibold text-slate-700">Contact Persons / Representatives</span>
                            <v-btn
                              text
                              small
                              color="primary"
                              class="normal-case font-semibold"
                              @click="addContactPerson"
                            >
                              <v-icon small left>mdi-account-plus</v-icon> Add Contact Person
                            </v-btn>
                          </div>

                          <div
                            v-for="(cp, cpIndex) in contactPerson"
                            :key="cpIndex"
                            class="border rounded-lg p-3 mb-3 bg-gray-50"
                          >
                            <div class="flex justify-between items-center mb-2">
                              <span class="text-xs font-bold text-slate-700">Contact Person #{{ cpIndex + 1 }}</span>
                              <v-btn
                                icon
                                small
                                color="red"
                                @click="removeContactPerson(cpIndex)"
                                v-if="contactPerson.length > 1"
                              >
                                <v-icon small>mdi-delete</v-icon>
                              </v-btn>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-2">
                              <v-text-field
                                v-model="cp.firstName"
                                label="First Name"
                                outlined
                                dense
                                hide-details
                                placeholder="ex : John"
                              ></v-text-field>
                              <v-text-field
                                v-model="cp.middleName"
                                label="Middle Name"
                                outlined
                                dense
                                hide-details
                                placeholder="ex : Doe"
                              ></v-text-field>
                              <v-text-field
                                v-model="cp.lastName"
                                label="Last Name"
                                outlined
                                dense
                                hide-details
                                placeholder="ex : Smith"
                              ></v-text-field>
                            </div>
                            <div class="grid grid-cols-1 sm:grid-cols-3 gap-2">
                              <v-text-field
                                v-model="cp.designation"
                                label="Designation / Role"
                                outlined
                                dense
                                hide-details
                                placeholder="ex : Director / Manager"
                              ></v-text-field>
                              <v-text-field
                                v-model="cp.contactPersonEmail"
                                label="Email"
                                outlined
                                dense
                                hide-details
                                placeholder="ex : john@company.com"
                              ></v-text-field>
                              <v-text-field
                                v-model="cp.contactPersonPhone"
                                label="Phone Number"
                                outlined
                                dense
                                hide-details
                                placeholder="ex : 9876543210"
                              ></v-text-field>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Individual Contacts Section -->
                      <template v-else>
                        <div class="grid grid-cols-2 gap-2">
                          <v-text-field
                            v-model="customerEmail"
                            label="Email"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : john@example.com"
                          ></v-text-field>
                          <v-text-field
                            v-model="customerMobile[0]"
                            label="Phone Number"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : 9876543210"
                          ></v-text-field>
                          <v-text-field
                            v-model="customerMobile[1]"
                            label="Alternate Phone Number"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : 9876543210"
                          ></v-text-field>
                          <v-text-field
                            v-model="emergencyName"
                            label="Emergency Contact Name"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : John Doe"
                          ></v-text-field>
                          <v-text-field
                            v-model="emergencyPhone"
                            label="Emergency Contact Number"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : 9876543210"
                          ></v-text-field>
                          <v-text-field
                            v-model="mailingAddress"
                            label="Mailing Address"
                            hide-details
                            outlined
                            dense
                            placeholder="ex : 123 Main St, City, State"
                          ></v-text-field>
                          <v-select
                            v-model="preferredContactMethod"
                            :items="['Email', 'Phone', 'SMS', 'Mail']"
                            label="Preferred Contact Method"
                            hint="How would you like to be reached?"
                            persistent-hint
                            hide-details
                            outlined
                            dense
                          ></v-select>
                        </div>
                      </template>
                    </div>

                    <div class="flex items-center pt-2 justify-end">
                      <v-btn @click="step--" outlined style="text-transform: none">Back</v-btn>
                      <v-btn
                        @click="step++"
                        class="white--text ml-3"
                        :color="$vuetify.theme.currentTheme.primary"
                        style="text-transform: none"
                      >
                        Next
                      </v-btn>
                    </div>
                  </v-stepper-content>

                  <!-- Step 6 (Individual) / Step 5 (Company): Financial Details -->
                  <v-stepper-step :complete="step > financialStepNum" :step="financialStepNum" editable>
                    Financial Details
                    <small>(Optional)</small>
                  </v-stepper-step>
                  <v-stepper-content :step="financialStepNum">
                    <v-checkbox
                      v-model="fillFinancialDetails"
                      label="Do you want to add financial details"
                      hide-details
                      class="mt-3 mb-3"
                    ></v-checkbox>

                    <div v-if="fillFinancialDetails">
                      <v-form>
                        <v-text-field
                          v-model="bankAccountNumber"
                          label="Bank Account Number"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: 1234567890"
                          class="mt-3 mb-3"
                        ></v-text-field>
                        <v-text-field
                          v-model="bankName"
                          label="Bank Name"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: HDFC Bank"
                          class="mt-3 mb-3"
                        ></v-text-field>
                        <v-text-field
                          v-model="ifscCode"
                          label="IFSC Code"
                          hide-details
                          outlined
                          dense
                          placeholder="ex: HDFC0001234"
                          class="mt-3 mb-3"
                        ></v-text-field>
                      </v-form>
                    </div>

                    <div class="flex items-center pt-2 justify-end">
                      <v-btn @click="step--" style="text-transform: none" outlined>Back</v-btn>
                    </div>
                  </v-stepper-content>
                </v-stepper-items>
              </v-stepper>
            </v-form>
          </div>
          <!-- Form bottom buttons - inside the card -->
          <!-- <div class="flex sticky bottom-0 z-50 bg-white p-2 items-center justify-end">
            <v-btn outlined @click="cancel">Cancel</v-btn>
            <v-btn
              @click="addCustomer"
              class="white--text ml-3"
              :color="$vuetify.theme.currentTheme.primary"
              >Save
            </v-btn>
          </div> -->

          <v-dialog v-model="showAddAddressDialog" width="auto">
            <CompanyAddress
              :company="crn"
              :isOpen="showAddAddressDialog"
              @cancel="closeAddressDialogs"
              @save="addLocation"
              title="Add Address"
            />
          </v-dialog>
          <v-dialog v-model="showAddRefDialog" width="auto">
            <CompanyAddress
              :company="crn"
              :isOpen="showAddRefDialog"
              @cancel="closeAddressDialogs"
              @save="addRefLocation"
              title="Add Address"
            />
          </v-dialog>

          <!-- Aadhaar OTP Dialog -->
          <v-dialog v-model="aadhaarOtpDialog" persistent max-width="420">
            <v-card class="pa-5 rounded-xl">
              <div class="flex items-center justify-between mb-4">
                <div class="flex items-center">
                  <v-icon color="primary" class="mr-2">mdi-shield-lock-outline</v-icon>
                  <h3 class="text-base font-bold text-gray-800">Verify Aadhaar</h3>
                </div>
                <v-btn icon small @click="aadhaarOtpDialog = false" :disabled="aadhaarLoading">
                  <v-icon size="20">mdi-close</v-icon>
                </v-btn>
              </div>
              <v-card-text class="px-0">
                <p class="text-xs text-gray-600 mb-4">
                  An OTP has been sent to the mobile number registered with this Aadhaar. Please enter the 6-digit OTP to complete verification.
                </p>
                <div class="flex justify-center my-4">
                  <PasscodeInput
                    v-if="aadhaarOtpDialog"
                    v-model="aadhaarOtp"
                    :otpField="6"
                    :autoFocus="true"
                    @keyup.enter.native="submitAadhaarOtp"
                  />
                </div>
              </v-card-text>
              <v-card-actions class="px-0 pt-2">
                <v-spacer></v-spacer>
                <v-btn
                  color="grey darken-1"
                  text
                  class="normal-case font-semibold"
                  :disabled="aadhaarLoading"
                  @click="aadhaarOtpDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn
                  color="primary"
                  class="white--text normal-case font-semibold px-5 rounded-lg"
                  :loading="aadhaarLoading"
                  :disabled="!aadhaarOtp || aadhaarOtp.length !== 6"
                  @click="submitAadhaarOtp"
                >
                  Verify OTP
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </template>
        <template #bulk>
          <GenericBulkUpload
            :title="'Bulk Customer Upload'"
            :subtitle="'Upload multiple customers using Excel file'"
            :entityName="'Customer'"
            @save-button-visibility="handleSaveButtonVisibility"
            :uploadEndpoint="'/customer/bulk'"
            :requiredColumns="['crn', 'First Name', 'Last Name', 'Customer Full Name']"
            :sampleData="[
              {
                crn: '123456',
                'First Name': 'John',
                'Last Name': 'Doe',
                'Full Name': 'John Doe',
                'Middle Name': 'M',
                'Client Name': 'ABC Corp',
                'Date of birth': '1990-01-01',
                Gender: 'MALE',
                'Customer Email': 'john@example.com',
                'Customer Number 1': '9999999999',
                'Customer Number 2': '8888888888',
                Occupation: 'Engineer',
                Company: 'Tech Corp',
                Jobtype: 'Full Time',
                Salary: '50000',
                'Father Name': 'James Doe',
                'Mother Name': 'Jane Doe',
                Entity: 'Company XYZ'
              }
            ]"
            :successKey="'customerCreatedSuccessfully'"
            :failedKey="'customerCreationFailed'"
            :invalidItemsKey="'invalidCustomers'"
            :enableSave="false"
            :controlParentSaveButton="true"
            :additionalPayload="{ caseType: caseConfig?.name, branch: caseConfig?.branch }"
            @uploadComplete="handleBulkUploadComplete"
            @close="setActiveType('create')"
          />
        </template>
      </ActitiveTabcomponent>
    </div>
    <div>
      <!-- Bulk Loan Create Form -->

      <div class="border-t px-5 py-3 bg-white dark-bg-custom">
        <div class="flex justify-end gap-3">
          <v-btn text @click="cancel" style="text-transform: none" class="cancel-btn">
            Cancel
          </v-btn>
          <v-btn
            v-if="isSaveButtonEnabled"
            color="primary"
            @click="addCustomer"
            :loading="saving"
            :disabled="saving"
            class="save-btn"
            style="text-transform: none"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
import { template } from 'lodash'
import CompanyAddress from '../CompanyAddress.vue'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import GenericBulkUpload from '../CollectionManagement/GenericBulkUpload.vue'
import PasscodeInput from '@/components/PasscodeInput.vue'
import { h } from 'vue'
import { axiosUrl } from '@/utils/workforceApi'
export default {
  name: 'AddEditCaseTask',
  components: {
    CompanyAddress,
    ActitiveTabcomponent,
    GenericBulkUpload,
    PasscodeInput
  },
  data() {
    return {
      step: 1,
      customerType: 'individual',
      companyName: '',
      fillContactInfo: false,
      fillOccupationDetails: false,
      fillEmploymentDetails: false,
      fillFinancialDetails: false,
      fillIdentityDetails: false,
      // Aadhaar OTP verification state
      aadhaarOtpDialog: false,
      aadhaarOtp: '',
      aadhaarLoading: false,
      aadhaarRefId: null,
      currentAadhaarDoc: null,
      identityDetails: [
        {
          idType: '',
          idNumber: '',
          nameAsperId: '',
          expiryDate: null,
          issueDate: null,
          issuedBy: '',
          issueDateMenu: false,
          expiryDateMenu: false
        }
      ],
      valid: true,
      tabkey: null,
      activeType: 'create',
      saving: false,
      genderTypes: [
        { text: 'Male', value: 'MALE' },
        { text: 'Female', value: 'FEMALE' },
        { text: 'Others', value: 'OTHERS' },
        { text: 'Not Applicable', value: 'NOT_APPLICABLE' }
      ],
      jobTypes: [
        { text: 'Salaried', value: 'SALARIED' },
        { text: 'Self Employed', value: 'SELF_EMPLOYED' },
        { text: 'Business', value: 'BUSINESS' },
        { text: 'Student', value: 'STUDENT' }
      ],
      employmentStatusOptions: [
        'Full-time',
        'Part-time',
        'Self-employed',
        'Unemployed',
        'Retired',
        'Contract',
        'Freelance',
        'Business Owner',
        'Student'
      ],
      addressTypes: [
        { text: 'Current Address', value: 'CURRENT_ADDRESS' },
        { text: 'Mailing Address', value: 'MAILING_ADDRESS' },
        { text: 'Billing Address', value: 'BILLING_ADDRESS' },
        { text: 'Office Address', value: 'OFFICE_ADDRESS' },
        { text: 'Residential Address', value: 'RESIDENTIAL_ADDRESS' }
      ],
      anyReferences: false,
      relation: null,
      showAddAddressDialog: false,
      showAddRefDialog: false,
      customerAddressFormat: '',
      addressTypeToAdd: null,
      selectedAddress: null,
      crn: '',
      firstName: '',
      middleName: '',
      lastName: '',
      dateOfBirth: null,
      gender: null,
      customerEmail: '',
      menu: false,
      customerMobile: ['', ''],
      caseConfig: null,
      handleBulkUploadComplete: () => {},
      customerDetails: [
        {
          typeOfAddress: null
        }
      ],
      contactPerson: [
        {
          firstName: '',
          middleName: '',
          lastName: '',
          designation: '',
          contactPersonEmail: '',
          contactPersonPhone: ''
        }
      ],
      references: [
        {
          name: '',
          relation: null,
          mobile: '',
          addresses: [{ addressLine1: '' }]
        }
      ],
      selectedIndex: null,
      jobType: null,
      occupation: '',
      company: '',
      salary: null,
      typeOfAddress: null,
      fatherName: '',
      motherName: '',
      stepperRule: true,
      nationality: '',
      maritalStatus: null,
      searchEntity: '',
      selectedEntity: null,
      rules: {
        crn: [(val) => (val || '').length > 0 || `CRN is required`]
      },
      // Add saved form data
      savedFormData: {
        crn: '',
        selectedEntity: null,
        customerEmail: '',
        customerMobile: '',
        fatherName: '',
        motherName: '',
        customerDetails: [],
        references: [],
        linkWithSubLicense: false,
        selectedSubcase: null
      },
      showForm: false,
      emergencyName: '',
      emergencyPhone: '',
      mailingAddress: '',
      preferredContactMethod: null,
      employmentStatus: null,
      employmentDuration: '',
      previousEmployment: '',
      industrySector: '',
      designation: '',
      monthlyIncome: null,
      yearlyIncome: null,
      taxPerYear: null,
      bankAccountNumber: '',
      bankName: '',
      ifscCode: '',
      isSaveButtonEnabled: true,
      relatedCategory: 'collection',
      subCase: null,
      subCasesList: []
    }
  },
  props: {
    editCustomer: Object,
    caseType: String,
    entities: Array,
    configTabMapping: Object
  },
  watch: {
    editCustomer: {
      handler(newVal) {
        this.$props.editCustomer = newVal
        this.populateFormData()
      },
      immediate: true
    },
    customerType(newVal, oldVal) {
      if (oldVal && newVal !== oldVal) {
        this.resetFormData(newVal)
      } else {
        const allowed = newVal === 'company' ? ['GSTIN', 'PAN'] : ['Aadhar', 'PAN', 'Driving License', 'Passport', 'Voter ID']
        this.identityDetails.forEach((id) => {
          if (id.idType && !allowed.includes(id.idType)) {
            id.idType = ''
            id.idNumber = ''
            id.rawIdNumber = ''
            id.nameAsperId = ''
            id.isVerfied = false
          }
        })
      }
    }
  },
  computed: {
    contactStepNum() {
      return this.customerType === 'individual' ? 5 : 4
    },
    financialStepNum() {
      return this.customerType === 'individual' ? 6 : 5
    },
    idTypeOptions() {
      if (this.customerType === 'company') {
        return ['GSTIN', 'PAN']
      }
      return ['Aadhar', 'PAN', 'Driving License', 'Passport', 'Voter ID']
    },
    mainTabs() {
      const tabs = [
        {
          label: `${this.editCustomer?._id ? 'Edit Customer' : 'Create Customer'}`,
          key: 'create',
          icon: `${this.editCustomer?._id ? 'mdi-pencil' : 'mdi-plus'}`,
          component: 'AddCollCustomer'
        },
        {
          label: `${this.editCustomer?._id ? 'Bulk Update' : 'Bulk create'}`,
          key: 'bulk',
          icon: 'mdi-upload',
          component: 'GenericBulkUpload'
        }
      ]
      return tabs
    },
    mobileRules() {
      return [(v) => (v && v.length === 10) || 'Phone Number should have 10 digits']
    },
    showAddressTypeButton() {
      return this.customerDetails?.length < this.addressTypes.length
    },
    dateOfBirthFormat() {
      if (this.dateOfBirth) {
        return this.$dayjs(this.dateOfBirth).format('DD-MM-YYYY')
      }
      return ''
    },
    isPersonalInfoCompleted() {
      if (this.customerType === 'company') {
        return Boolean(this.crn && this.companyName)
      }
      return Boolean(
        this.crn &&
        this.firstName &&
        this.lastName &&
        this.gender
      )
    },
    isAdditionalInfoCompleted() {
      return (
        !this.fillContactInfo ||
        (this.jobType && this.occupation && this.fatherName && this.motherName)
      )
    },
    jumpToStep() {
      return !this.isPersonalInfoCompleted ? 1 : this.step
    }
  },
  created() {
    this.fetchSubCases()
    if (!this.editCustomer?._id && !this.crn) {
      this.crn = this.generateCRN()
    }
  },
  methods: {
    generateCRN() {
      const timestamp = Date.now().toString().slice(-6)
      const random = Math.floor(1000 + Math.random() * 9000)
      return `CRN${timestamp}${random}`
    },
    addContactPerson() {
      this.contactPerson.push({
        firstName: '',
        middleName: '',
        lastName: '',
        designation: '',
        contactPersonEmail: '',
        contactPersonPhone: ''
      })
    },
    removeContactPerson(index) {
      this.contactPerson.splice(index, 1)
    },
    setActiveType(type) {
      if (this.activeType === 'bulk' && type === 'create') {
        this.resetFormData()
      }
      this.activeType = type
      if (type === 'create') {
        this.isSaveButtonEnabled = true
      }
    },
    handleSaveButtonVisibility(shouldShow) {
      if (this.activeType === 'bulk') {
        this.isSaveButtonEnabled = shouldShow
      }
    },
    async addCustomer() {
      try {
        this.saving = true
        const isValid = this.$refs.form.validate()
        if (!isValid) {
          this.stepperRule = false
          this.saving = false
          return
        }

        const payload = {
          crn: this.crn,
          customerType: this.customerType || 'individual',
          relatedSubCategories: this.caseType || undefined,
          relatedCategory: this.relatedCategory || 'collection',
          subCase: this.subCase || undefined,
          customerEmail: this.customerEmail || null,
          customerMobile: this.customerMobile.filter(Boolean).length
            ? this.customerMobile.filter(Boolean)
            : undefined
        }

        if (this.customerType === 'company') {
          payload.companyName = this.companyName || undefined
          payload.customerFullName = this.companyName || undefined
          const validContacts = (this.contactPerson || []).filter(
            (cp) => cp.firstName || cp.lastName || cp.contactPersonPhone || cp.contactPersonEmail
          )
          if (validContacts.length) {
            payload.contactPerson = validContacts
          }
        } else {
          payload.firstName = this.firstName || undefined
          payload.middleName = this.middleName || undefined
          payload.lastName = this.lastName || undefined
          payload.customerFullName = [this.firstName, this.middleName, this.lastName]
            .filter(Boolean)
            .join(' ')
          payload.dateOfBirth = this.dateOfBirth || undefined
          payload.gender = this.gender || undefined
          payload.maritalStatus = this.maritalStatus || undefined
          payload.nationality = this.nationality || undefined
          payload.jobType = this.jobType || undefined
          payload.occupation = this.occupation || undefined
          payload.company = this.company || undefined
          payload.salary = this.salary || undefined
          payload.fatherName = this.fatherName || undefined
          payload.motherName = this.motherName || undefined
          payload.emergencyName = this.emergencyName || undefined
          payload.emergencyPhone = this.emergencyPhone || undefined
          payload.mailingAddress = this.mailingAddress || undefined
          payload.preferredContactMethod = this.preferredContactMethod || undefined
          payload.employmentStatus = this.employmentStatus || undefined
          payload.employmentDuration = this.employmentDuration || undefined
          payload.previousEmployment = this.previousEmployment || undefined
          payload.industrySector = this.industrySector || undefined
          payload.designation = this.designation || undefined
          payload.monthlyIncome = this.monthlyIncome || undefined
          payload.yearlyIncome = this.yearlyIncome || undefined
          payload.taxPerYear = this.taxPerYear || undefined
        }

        if (this.selectedEntity) {
          payload.entity = this.selectedEntity
        }
        if (this.customerDetails.length && this.customerDetails[0].addressLine1) {
          payload.customerAddress = this.customerDetails
        }
        if (this.references.length && this.anyReferences) {
          payload.references = this.references
        }
        if (this.fillIdentityDetails) {
          payload.identityDetails = this.identityDetails.map((id) => ({
            idType: id.idType,
            idNumber: id.rawIdNumber || id.idNumber,
            nameAsperId: id.nameAsperId,
            expiryDate: id.expiryDate,
            issueDate: id.issueDate,
            issuedBy: id.issuedBy,
            isVerfied: Boolean(id.isVerfied),
            verifiedBy: id.verifiedBy || (id.isVerfied ? 'Decentro / System' : undefined),
            verifiedOn: id.verifiedOn || (id.isVerfied ? new Date() : undefined)
          }))
        }
        if (this.fillFinancialDetails && (this.bankAccountNumber || this.bankName || this.ifscCode)) {
          payload.bankDetails = {
            accountNumber: this.bankAccountNumber || undefined,
            bankName: this.bankName || undefined,
            ifscCode: this.ifscCode || undefined
          }
        }

        let url = axiosUrl.customers.postCustomer
        let method = 'POST'

        if (this.editCustomer) {
          url += `/${this.editCustomer._id}`
          method = 'PUT'
        }

        const response = await this.$axios({
          method: method,
          url: url,
          data: payload
        })

        if (response.data.success) {
          this.$toast.success(`Customer ${this.editCustomer ? 'updated' : 'added'} successfully`)
          this.cancel()
          this.resetFormData()
        } else {
          this.$toast.error(response.data.message || 'Failed to save customer')
        }
      } catch (error) {
        console.error('Error saving customer:', error)
        this.$toast.error(
          error.response?.data?.message || 'An error occurred while saving customer'
        )
      } finally {
        this.saving = false
      }
    },
    async addLocation(data) {
      const index = this.selectedIndex
      if (index !== null && index !== undefined) {
        const newAddress = {
          addressLine1: `${data.addLine1}, ${data.addLine2}, ${data.city}, ${data.state}, ${data.pinCode}`,
          addressLine2: data.addLine2,
          city: data.city,
          state: data.state,
          country: data.country,
          pinCode: data.pinCode
        }
        this.$set(this.customerDetails, index, {
          typeOfAddress: this.customerDetails[index].typeOfAddress,
          ...newAddress
        })
        this.showAddAddressDialog = false
      }
    },
    async addRefLocation(data) {
      const index = this.selectedRefIndex
      const addresses = []
      if (index !== null && index !== undefined) {
        const newAddress = {
          addressLine1: `${data.addLine1}, ${data.addLine2}, ${data.city}, ${data.state}, ${data.pinCode}`,
          addressLine2: data.addLine2,
          city: data.city,
          state: data.state,
          country: data.country,
          pinCode: data.pinCode
        }
        addresses.push(newAddress)
        this.$set(this.references, index, {
          name: this.references[index].name,
          relation: this.references[index].relation,
          mobile: this.references[index].mobile,
          addresses: addresses
        })
        this.showAddRefDialog = false
      }
    },
    debounceEntity() {
      if (!this.searchEntity || this.selectedManager) return
      this.$emit('emittedEntity', this.searchEntity)
    },
    addReference() {
      this.references.push({
        name: '',
        relation: null,
        mobile: '',
        addresses: [{ addressLine1: '' }]
      })
    },
    removeChip(item, options) {
      const index = options.indexOf(item)
      if (index !== -1) {
        options.splice(index, 1)
      }
    },
    filterAddressTypes(index) {
      const selectedTypes = this.customerDetails.map((item) => item.typeOfAddress)
      return this.addressTypes.filter(
        (type) =>
          !selectedTypes.includes(type.value) ||
          type.value === this.customerDetails[index].typeOfAddress
      )
    },
    addCustomerDetails() {
      this.customerDetails.push({
        typeOfAddress: null
      })
    },
    closeAddressDialogs() {
      this.showAddAddressDialog = false
      this.showAddRefDialog = false
    },
    removeReference(index) {
      this.references.splice(index, 1)
    },
    removeDetails(index) {
      this.customerDetails.splice(index, 1)
    },
    showAddAddress(index) {
      this.selectedIndex = index
      this.showAddAddressDialog = true
    },
    showAddRefAddress(index) {
      this.selectedRefIndex = index
      this.showAddRefDialog = true
    },
    cancel() {
      this.activeType = 'create'
      this.$emit('customerAdded')
      this.step = 1
      this.clearForm()
    },

    cancelForm() {
      this.clearForm()
      this.cancel()
    },

    populateFormData() {
      if (this.editCustomer) {
        this.customerType = this.editCustomer.customerType || 'individual'
        this.companyName = this.editCustomer.companyName || ''
        this.crn = this.editCustomer.crn || ''
        this.firstName = this.editCustomer.firstName || this.editCustomer?.customerFullName
        this.relatedCategory = this.editCustomer.relatedCategory || 'collection'
        this.subCase = this.editCustomer.subCase?._id || this.editCustomer.subCase || null
        this.middleName = this.editCustomer.middleName || ''
        this.lastName = this.editCustomer.lastName || ''
        this.dateOfBirth = this.editCustomer.dateOfBirth || null
        this.gender = this.editCustomer.gender || null
        this.maritalStatus = this.editCustomer.maritalStatus || null
        this.nationality = this.editCustomer.nationality || ''
        this.customerEmail = this.editCustomer.customerEmail || ''
        if (Array.isArray(this.editCustomer.customerMobile)) {
          this.customerMobile = [
            this.editCustomer.customerMobile[0] || '',
            this.editCustomer.customerMobile[1] || ''
          ]
        } else if (typeof this.editCustomer.customerMobile === 'string') {
          this.customerMobile = [this.editCustomer.customerMobile, '']
        } else {
          this.customerMobile = ['', '']
        }
        this.jobType = this.editCustomer.jobType || null
        this.occupation = this.editCustomer.occupation || ''
        this.company = this.editCustomer.company || ''
        this.salary = this.editCustomer.salary || null
        this.fatherName = this.editCustomer.fatherName || ''
        this.motherName = this.editCustomer.motherName || ''

        this.emergencyName = this.editCustomer.emergencyName || ''
        this.emergencyPhone = this.editCustomer.emergencyPhone || ''
        this.mailingAddress = this.editCustomer.mailingAddress || ''
        this.preferredContactMethod = this.editCustomer.preferredContactMethod || null
        this.employmentStatus = this.editCustomer.employmentStatus || null
        this.employmentDuration = this.editCustomer.employmentDuration || ''
        this.previousEmployment = this.editCustomer.previousEmployment || ''
        this.industrySector = this.editCustomer.industrySector || ''
        this.designation = this.editCustomer.designation || ''
        this.monthlyIncome = this.editCustomer.monthlyIncome || null
        this.yearlyIncome = this.editCustomer.yearlyIncome || null
        this.taxPerYear = this.editCustomer.taxPerYear || null

        // Contact persons for company
        if (this.editCustomer.contactPerson && this.editCustomer.contactPerson.length > 0) {
          this.contactPerson = this.editCustomer.contactPerson.map((cp) => ({
            firstName: cp.firstName || '',
            middleName: cp.middleName || '',
            lastName: cp.lastName || '',
            designation: cp.designation || '',
            contactPersonEmail: cp.contactPersonEmail || '',
            contactPersonPhone: cp.contactPersonPhone || ''
          }))
        } else {
          this.contactPerson = [
            {
              firstName: '',
              middleName: '',
              lastName: '',
              designation: '',
              contactPersonEmail: '',
              contactPersonPhone: ''
            }
          ]
        }

        // Auto-enable sections if data exists
        if (
          this.editCustomer.customerEmail ||
          this.editCustomer.customerMobile?.length ||
          (this.editCustomer.contactPerson && this.editCustomer.contactPerson.length > 0)
        ) {
          this.fillContactInfo = true
        }
        if (this.editCustomer.occupation || this.editCustomer.company) {
          this.fillOccupationDetails = true
        }
        if (
          this.editCustomer.employmentStatus ||
          this.editCustomer.employmentDuration ||
          this.editCustomer.previousEmployment ||
          this.editCustomer.company ||
          this.editCustomer.occupation ||
          this.editCustomer.designation ||
          this.editCustomer.monthlyIncome ||
          this.editCustomer.yearlyIncome ||
          this.editCustomer.jobType ||
          this.editCustomer.salary
        ) {
          this.fillEmploymentDetails = true
        }

        // Financial details
        if (this.editCustomer.bankDetails) {
          this.fillFinancialDetails = true
          this.bankAccountNumber = this.editCustomer.bankDetails.accountNumber || ''
          this.bankName = this.editCustomer.bankDetails.bankName || ''
          this.ifscCode = this.editCustomer.bankDetails.ifscCode || ''
        }

        // Populate customer addresses
        if (this.editCustomer.customerAddress && this.editCustomer.customerAddress.length > 0) {
          this.customerDetails = this.editCustomer.customerAddress.map((address) => ({
            typeOfAddress: address.typeOfAddress || null,
            addressLine1: address.addressLine1 || '',
            addressLine2: address.addressLine2 || '',
            city: address.city || '',
            state: address.state || '',
            country: address.country || '',
            pinCode: address.pinCode || ''
          }))
        }

        // Populate identity details
        if (this.editCustomer.identityDetails && this.editCustomer.identityDetails.length > 0) {
          this.fillIdentityDetails = true
          this.identityDetails = this.editCustomer.identityDetails.map((id) => ({
            idType: id.idType || '',
            idNumber: id.idNumber || '',
            nameAsperId: id.nameAsperId || '',
            expiryDate: id.expiryDate ? this.$dayjs(id.expiryDate).format('YYYY-MM-DD') : null,
            issueDate: id.issueDate ? this.$dayjs(id.issueDate).format('YYYY-MM-DD') : null,
            issuedBy: id.issuedBy || '',
            isVerfied: Boolean(id.isVerfied),
            verifiedBy: id.verifiedBy || '',
            verifiedOn: id.verifiedOn || null,
            issueDateMenu: false,
            expiryDateMenu: false
          }))

          // If any idNumber is masked, fetch decrypted real numbers
          if (
            this.editCustomer._id &&
            this.identityDetails.some(
              (id) => id.idNumber.includes('•') || id.idNumber.includes('XX')
            )
          ) {
            this.$axios
              .get(`/customer/${this.editCustomer._id}/identity-details`)
              .then((res) => {
                const decList = res.data?.data?.identityDetails || []
                this.identityDetails.forEach((id, idx) => {
                  const match =
                    decList[idx] ||
                    decList.find(
                      (d) => (d.idType || '').toLowerCase() === (id.idType || '').toLowerCase()
                    )
                  if (
                    match &&
                    match.idNumber &&
                    !match.idNumber.includes('•') &&
                    !match.idNumber.includes('XX')
                  ) {
                    id.idNumber = match.idNumber
                  }
                })
              })
              .catch((err) => {
                console.error('Error fetching decrypted identity numbers for edit:', err)
              })
          }
        }

        // Populate references
        if (this.editCustomer.references && this.editCustomer.references.length > 0) {
          this.anyReferences = true
          this.references = this.editCustomer.references.map((reference) => ({
            name: reference.name || '',
            relation: reference.relation || null,
            mobile: reference.mobile || '',
            addresses: (reference.addresses || []).map((address) => ({
              addressLine1: address.addressLine1 || '',
              addressLine2: address.addressLine2 || '',
              city: address.city || '',
              state: address.state || '',
              country: address.country || '',
              pinCode: address.pinCode || ''
            }))
          }))
        }
      }
    },

    resetFormData(forcedType) {
      this.customerType = forcedType || this.customerType || 'individual'
      this.companyName = ''
      this.crn = !this.editCustomer?._id ? this.generateCRN() : ''
      this.firstName = ''
      this.relatedCategory = 'collection'
      this.subCase = null
      this.middleName = ''
      this.lastName = ''
      this.dateOfBirth = null
      this.gender = null
      this.customerEmail = ''
      this.customerMobile = ['', '']
      this.fatherName = ''
      this.motherName = ''
      this.nationality = ''
      this.maritalStatus = null
      this.selectedEntity = null
      this.jobType = null
      this.occupation = ''
      this.company = ''
      this.salary = null
      this.emergencyName = ''
      this.emergencyPhone = ''
      this.mailingAddress = ''
      this.preferredContactMethod = null
      this.employmentStatus = null
      this.employmentDuration = ''
      this.previousEmployment = ''
      this.industrySector = ''
      this.designation = ''
      this.monthlyIncome = null
      this.yearlyIncome = null
      this.taxPerYear = null
      this.bankAccountNumber = ''
      this.bankName = ''
      this.ifscCode = ''
      this.customerDetails = [{ typeOfAddress: null }]
      this.contactPerson = [
        {
          firstName: '',
          middleName: '',
          lastName: '',
          designation: '',
          contactPersonEmail: '',
          contactPersonPhone: ''
        }
      ]
      this.references = [
        {
          name: '',
          relation: null,
          mobile: '',
          addresses: [{ addressLine1: '' }]
        }
      ]
      this.identityDetails = [
        {
          idType: '',
          idNumber: '',
          nameAsperId: '',
          expiryDate: null,
          issueDate: null,
          issuedBy: '',
          issueDateMenu: false,
          expiryDateMenu: false
        }
      ]
      this.fillContactInfo = false
      this.fillOccupationDetails = false
      this.fillEmploymentDetails = false
      this.fillFinancialDetails = false
      this.fillIdentityDetails = false
      this.anyReferences = false
      this.step = 1
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    clearForm() {
      this.resetFormData()
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },

    calculateYearlyIncome() {
      this.yearlyIncome = this.monthlyIncome * 12
    },
    addIdentityDetail() {
      this.identityDetails.push({
        idType: '',
        idNumber: '',
        nameAsperId: '',
        expiryDate: null,
        issueDate: null,
        issuedBy: '',
        issueDateMenu: false,
        expiryDateMenu: false
      })
    },
    removeIdentityDetail(index) {
      this.identityDetails.splice(index, 1)
    },
    async fetchSubCases() {
      try {
        const res = await this.$axios.get('/orgSetup/extend-usecase')
        this.subCasesList = res.data?.data || []
      } catch (error) {
        console.error('Error fetching sub cases:', error)
      }
    },
    formatMaskedId(idNumber) {
      if (!idNumber) return ''
      const clean = String(idNumber).trim()
      if (clean.includes('•')) return clean
      if (clean.length <= 4) return clean
      const first2 = clean.slice(0, 2)
      const last2 = clean.slice(-2)
      const maskLength = Math.max(clean.length - 4, 4)
      return `${first2}${'•'.repeat(maskLength)}${last2}`
    },
    verifyIdentityDoc(idDoc) {
      if (idDoc.idType === 'GSTIN') {
        return this.verifyGstForDoc(idDoc)
      } else if (idDoc.idType === 'PAN') {
        return this.verifyPanForDoc(idDoc)
      } else if (['Aadhar', 'Aadhaar'].includes(idDoc.idType)) {
        return this.verifyAadhaarForDoc(idDoc)
      }
    },
    async verifyAadhaarForDoc(idDoc) {
      const rawNum = idDoc.rawIdNumber || idDoc.idNumber || ''
      const cleanAadhaar = String(rawNum).replace(/\D/g, '')
      if (!cleanAadhaar || cleanAadhaar.length !== 12) {
        this.$toast.error('Please enter a valid 12-digit Aadhaar Number')
        return
      }

      this.$set(idDoc, 'verifying', true)
      try {
        const res = await this.$axios.post('/customer/aadhaar/otp', {
          aadhaarNumber: cleanAadhaar
        })
        if (res.data?.success && res.data?.data) {
          this.aadhaarRefId = res.data.data.reference_id || res.data.data.referenceId
          this.aadhaarOtp = ''
          this.currentAadhaarDoc = idDoc
          this.aadhaarOtpDialog = true
          this.$toast.info('OTP sent to Aadhaar-registered mobile number.')
        } else {
          this.$toast.error(
            res.data?.data?.message || res.data?.message || 'Failed to send Aadhaar OTP'
          )
        }
      } catch (err) {
        console.error('Aadhaar OTP error:', err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to send Aadhaar OTP')
      } finally {
        this.$set(idDoc, 'verifying', false)
      }
    },
    async submitAadhaarOtp() {
      if (
        !this.aadhaarOtp ||
        this.aadhaarOtp.length !== 6 ||
        !this.aadhaarRefId ||
        !this.currentAadhaarDoc
      ) {
        return
      }

      this.aadhaarLoading = true
      try {
        const idDoc = this.currentAadhaarDoc
        const rawNum = (idDoc.rawIdNumber || idDoc.idNumber || '').replace(/\D/g, '')
        const res = await this.$axios.post('/customer/aadhaar/verify', {
          referenceId: this.aadhaarRefId,
          otp: this.aadhaarOtp.trim()
        })

        if (res.data?.success && res.data?.data && res.data.data.success !== false) {
          const aData = res.data.data
          idDoc.rawIdNumber = rawNum
          this.$set(idDoc, 'idNumber', this.formatMaskedId(idDoc.rawIdNumber))
          this.$set(idDoc, 'isVerfied', true)
          this.$set(idDoc, 'verifiedLegalName', aData.name)
          if (aData.name) {
            this.$set(idDoc, 'nameAsperId', aData.name)
          }
          // Note: Issue date and expiry date are intentionally NOT auto-set to avoid invalid dates
          if (aData.dob && this.customerType === 'individual' && !this.dateOfBirth) {
            this.dateOfBirth = this.$dayjs(aData.dob).format('YYYY-MM-DD')
          }
          if (aData.gender && this.customerType === 'individual' && !this.gender) {
            const g = aData.gender.toUpperCase()
            this.gender = g.startsWith('M') ? 'MALE' : g.startsWith('F') ? 'FEMALE' : 'OTHERS'
          }
          this.$set(idDoc, 'issuedBy', 'UIDAI / Government of India')

          // Auto-populate customer address if empty
          if (aData.address && this.customerDetails && this.customerDetails[0]) {
            const addr = aData.address
            const line1 = typeof addr === 'string' ? addr : [addr.house, addr.street, addr.loc, addr.landmark].filter(Boolean).join(', ')
            if (!this.customerDetails[0].addressLine1 && line1) {
              this.$set(this.customerDetails[0], 'addressLine1', line1)
            }
            if (!this.customerDetails[0].city && (addr.dist || addr.city)) {
              this.$set(this.customerDetails[0], 'city', addr.dist || addr.city)
            }
            if (!this.customerDetails[0].state && addr.state) {
              this.$set(this.customerDetails[0], 'state', addr.state)
            }
            if (!this.customerDetails[0].pinCode && (addr.pc || addr.pincode)) {
              this.$set(this.customerDetails[0], 'pinCode', addr.pc || addr.pincode)
            }
          }

          this.aadhaarOtpDialog = false
          this.$toast.success(`Aadhaar verified: ${aData.name || 'Valid'}`)
        } else {
          this.$toast.error(
            res.data?.data?.message || res.data?.message || 'Aadhaar verification failed'
          )
        }
      } catch (err) {
        console.error('Aadhaar OTP Verify error:', err)
        this.$toast.error(
          err.response?.data?.message || err.message || 'Failed to verify Aadhaar OTP'
        )
      } finally {
        this.aadhaarLoading = false
      }
    },
    async verifyPanForDoc(idDoc) {
      const rawNum = idDoc.rawIdNumber || idDoc.idNumber || ''
      if (!rawNum || rawNum.trim().length !== 10) {
        this.$toast.error('Please enter a valid 10-character PAN')
        return
      }
      this.$set(idDoc, 'verifying', true)
      try {
        const res = await this.$axios.post('/customer/verify-pan', {
          pan: rawNum.trim().toUpperCase()
        })
        if (res.data?.success && res.data?.data && res.data.data.success !== false) {
          const pData = res.data.data
          idDoc.rawIdNumber = rawNum.trim().toUpperCase()
          this.$set(idDoc, 'idNumber', this.formatMaskedId(idDoc.rawIdNumber))
          this.$set(idDoc, 'isVerfied', true)
          this.$set(idDoc, 'verifiedLegalName', pData.name)
          if (pData.name) {
            this.$set(idDoc, 'nameAsperId', pData.name)
          }
          this.$set(idDoc, 'issuedBy', 'Income Tax Department / Government of India')
          this.$toast.success(`PAN verified: ${pData.name || 'Valid'}`)
        } else {
          this.$toast.error(res.data?.data?.message || res.data?.message || 'PAN verification failed')
        }
      } catch (err) {
        console.error('PAN Verification error:', err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to verify PAN')
      } finally {
        this.$set(idDoc, 'verifying', false)
      }
    },
    async verifyGstForDoc(idDoc) {
      const rawNum = idDoc.rawIdNumber || idDoc.idNumber || ''
      if (!rawNum || rawNum.trim().length !== 15) {
        this.$toast.error('Please enter a valid 15-character GST Number')
        return
      }
      this.$set(idDoc, 'verifying', true)
      try {
        const res = await this.$axios.post('/customer/verify-gst', {
          gstin: rawNum.trim().toUpperCase()
        })
        if (res.data?.success && res.data?.data && res.data.data.success !== false) {
          const gData = res.data.data
          idDoc.rawIdNumber = rawNum.trim().toUpperCase()
          this.$set(idDoc, 'idNumber', this.formatMaskedId(idDoc.rawIdNumber))
          this.$set(idDoc, 'isVerfied', true)
          this.$set(idDoc, 'verifiedLegalName', gData.legalName || gData.tradeName)
          if (gData.legalName) {
            this.$set(idDoc, 'nameAsperId', gData.legalName)
          }
          if (this.customerType === 'company' && !this.companyName && (gData.legalName || gData.tradeName)) {
            this.companyName = gData.legalName || gData.tradeName
          }
          // Note: Issue date and expiry date are intentionally NOT auto-set to avoid invalid dates
          this.$set(idDoc, 'issuedBy', 'GSTN / Government of India')

          // Auto-populate customer address if empty
          if (gData.address && this.customerDetails && this.customerDetails[0]) {
            if (!this.customerDetails[0].addressLine1 && gData.address.addressLine1) {
              this.$set(this.customerDetails[0], 'addressLine1', gData.address.addressLine1)
            }
            if (!this.customerDetails[0].city && gData.address.city) {
              this.$set(this.customerDetails[0], 'city', gData.address.city)
            }
            if (!this.customerDetails[0].state && gData.address.state) {
              this.$set(this.customerDetails[0], 'state', gData.address.state)
            }
            if (!this.customerDetails[0].pinCode && gData.address.pincode) {
              this.$set(this.customerDetails[0], 'pinCode', gData.address.pincode)
            }
          }

          this.$toast.success(`GSTIN verified: ${gData.legalName || gData.tradeName || 'Active'}`)
        } else {
          this.$toast.error(res.data?.data?.message || res.data?.message || 'GSTIN verification failed')
        }
      } catch (err) {
        console.error('GST Verification error:', err)
        this.$toast.error(err.response?.data?.message || err.message || 'Failed to verify GSTIN')
      } finally {
        this.$set(idDoc, 'verifying', false)
      }
    }
  }
}
</script>
<style scoped>
.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #f8f9fa;
}
.form-content {
  max-height: 70vh;
  overflow-y: auto;
}
/* Custom styling for stepper completed steps */
::v-deep .v-stepper__step--complete .v-stepper__step__step {
  background-color: #4caf50 !important; /* Green color for completed steps */
}

::v-deep .v-stepper__step--complete .v-stepper__label {
  color: #4caf50 !important; /* Green color for completed step labels */
}

::v-deep .v-stepper__step--active .v-stepper__step__step {
  background-color: #062783 !important; /* Keep active step blue */
}
.v-application--is-ltr .v-stepper--vertical .v-stepper__content {
  padding: 10px 60px 16px 23px;
}
.v-stepper--vertical .v-stepper__step {
  padding: 24px 24px 10px;
}
::v-deep .v-stepper--vertical .v-stepper__step .v-stepper__label {
  width: 100%;
}
.div {
  margin-right: 0px;
}
</style>
